import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, ArrowLeft, Wand2, MailCheck, KeyRound } from 'lucide-react';
import { SiDropbox, SiSnapchat, SiTiktok, SiTwitch } from 'react-icons/si';
import Logo from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { mockData } from '../mock';
import { api, formatApiError, pictureUrl } from '../lib/api';
import { useT } from '../context/LanguageContext';
import { Seo } from '../components/Seo';
import { useContent } from '../context/ContentContext';
import { DEFAULT_LOGIN_METHODS } from '../components/admin/SiteContentEditor';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog';

const DiscordIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.2.4a18 18 0 0 1 4.4 1.4 15 15 0 0 0-15.2 0A18 18 0 0 1 8.8 3.4L8.6 3a19.8 19.8 0 0 0-4.9 1.5C.6 9.1-.3 13.7.2 18.2a20 20 0 0 0 6 3l1.3-2.1a13 13 0 0 1-2.1-1l.5-.4a14.3 14.3 0 0 0 12.2 0l.5.4a13 13 0 0 1-2.1 1l1.3 2.1a20 20 0 0 0 6-3c.6-5.2-.9-9.8-3.5-13.8ZM8.3 15.5c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4Zm7.4 0c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4Z"/></svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.9 2H22l-7.3 8.3L23.2 22h-6.7l-5.2-6.8L5.3 22H2.2l7.8-8.9L1.8 2h6.9l4.7 6.2L18.9 2Zm-1.1 18.1h1.7L7.1 3.8H5.3l12.5 16.3Z"/></svg>
);

const SpotifyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24Zm5.5 17.3a.75.75 0 0 1-1 .25c-2.8-1.7-6.4-2.1-10.6-1.2a.75.75 0 1 1-.3-1.5c4.6-1 8.5-.6 11.7 1.4.3.2.4.7.2 1Zm1.5-3.3a.94.94 0 0 1-1.3.3c-3.2-2-8.2-2.6-12-1.4a.94.94 0 0 1-.6-1.8c4.4-1.3 9.8-.7 13.6 1.6.4.3.6.9.3 1.3Zm.1-3.4C15.3 8.3 8.9 8.1 5.2 9.2a1.1 1.1 0 1 1-.7-2.2c4.3-1.3 11.4-1 15.8 1.6a1.1 1.1 0 0 1-1.2 1.9Z"/></svg>
);

const GitHubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 .5a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2.2c-3.3.7-4-1.4-4-1.4-.6-1.4-1.4-1.8-1.4-1.8-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1.1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.7-.3-5.5-1.3-5.5-5.9 0-1.3.5-2.4 1.2-3.2-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.7.2 2.9.1 3.2.8.8 1.2 1.9 1.2 3.2 0 4.6-2.8 5.6-5.5 5.9.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .5Z"/></svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
    <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

const OAUTH_BUTTONS = [
  { id: 'google', label: 'Fortsett med Google', icon: <GoogleIcon />, className: 'bg-white text-black hover:bg-white/90' },
  { id: 'snapchat', label: 'Snapchat', icon: <SiSnapchat size={18} aria-hidden="true" />, className: 'bg-[#FFFC00] text-black hover:bg-[#e6e200]' },
  { id: 'discord', label: 'Discord', icon: <DiscordIcon />, className: 'bg-[#5865F2] text-white hover:bg-[#4752c4]' },
  { id: 'x', label: 'X', icon: <XIcon />, className: 'bg-white/10 border border-white/20 text-white hover:bg-white/20' },
  { id: 'spotify', label: 'Spotify', icon: <SpotifyIcon />, className: 'bg-[#1DB954] text-black hover:bg-[#1ed760]' },
  { id: 'github', label: 'GitHub', icon: <GitHubIcon />, className: 'bg-[#24292f] border border-white/20 text-white hover:bg-[#32383f]' },
  { id: 'twitch', label: 'Twitch', icon: <SiTwitch size={18} aria-hidden="true" />, className: 'bg-[#9146FF] text-white hover:bg-[#772CE8]' },
  { id: 'tiktok', label: 'TikTok', icon: <SiTiktok size={18} aria-hidden="true" />, className: 'bg-black border border-white/20 text-white hover:bg-white/10' },
  { id: 'dropbox', label: 'Dropbox', icon: <SiDropbox size={18} aria-hidden="true" />, className: 'bg-[#0061FF] text-white hover:bg-[#0052d9]' },
  { id: 'yahoo', label: 'Yahoo', icon: <span className="text-lg font-black italic leading-none" aria-hidden="true">y!</span>, className: 'bg-[#6001D2] text-white hover:bg-[#5000b8]' },
];

const inputCls = 'w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#ff2e2e] focus:outline-none focus:bg-white/10 transition-colors';

const buttonTextColor = (hex) => {
  const channels = hex.match(/[\da-f]{2}/gi)?.map((channel) => Number.parseInt(channel, 16) / 255) || [];
  if (channels.length !== 3) return '#fff';
  const linear = channels.map((channel) => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  return luminance > 0.179 ? '#111' : '#fff';
};

const Login = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const { login, loginWithPasskey, registerWithPasskey, register, requestMagicLink, loginWithMagicToken, checkAuth, startOAuthLogin } = useAuth();
  const { content } = useContent();
  const [searchParams] = useSearchParams();
  const magicHandled = useRef(false);
  const [magicSent, setMagicSent] = useState(false);
  const [oauthProviders, setOauthProviders] = useState([]);
  const [oauthProvidersUnavailable, setOauthProvidersUnavailable] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStage, setResetStage] = useState('request');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetNotice, setResetNotice] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const [mode, setMode] = useState('login');
  const [signupMethod, setSignupMethod] = useState('password');
  const [emailLoginOpen, setEmailLoginOpen] = useState(false);
  const [emailLoginIdentifier, setEmailLoginIdentifier] = useState('');
  const [emailLoginPassword, setEmailLoginPassword] = useState('');
  const [emailLoginStage, setEmailLoginStage] = useState('identifier');
  const [emailLoginError, setEmailLoginError] = useState('');
  const [emailLoginBusy, setEmailLoginBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/auth/oauth/providers', { timeout: 12000 })
      .then(({ data }) => {
        setOauthProviders(Array.isArray(data) ? data : []);
        setOauthProvidersUnavailable(false);
      })
      .catch(() => {
        setOauthProviders([]);
        setOauthProvidersUnavailable(true);
      });
  }, []);

  const methodSettings = content.site?.loginMethods || DEFAULT_LOGIN_METHODS;
  const methodSettingsByProvider = new Map(methodSettings.map((method) => [method.provider, method]));
  const visibleOAuthMethods = OAUTH_BUTTONS.filter(({ id }) => (
    oauthProviders.includes(id) && methodSettingsByProvider.get(id)?.enabled
  ));
  const sortByMethodOrder = (buttons) => buttons.sort((a, b) => {
    const orderA = Number(methodSettingsByProvider.get(a.id)?.order) || OAUTH_BUTTONS.findIndex((button) => button.id === a.id) + 1;
    const orderB = Number(methodSettingsByProvider.get(b.id)?.order) || OAUTH_BUTTONS.findIndex((button) => button.id === b.id) + 1;
    return orderA - orderB;
  });
  const renderOAuthButton = ({ id, label, icon, className }) => {
    const method = methodSettingsByProvider.get(id) || {};
    const large = method.size === 'large';
    const logo = method.logo ? pictureUrl(method.logo) : null;
    return (
      <button key={id} type="button" data-testid={`${id}-login-button`} onClick={() => startOAuthLogin(id).catch((e) => setError(formatApiError(e)))} style={method.color ? { backgroundColor: method.color, color: buttonTextColor(method.color) } : undefined} className={`w-full ${large ? 'py-3.5' : 'py-3'} rounded-full font-bold flex items-center justify-center ${large ? 'gap-3' : 'gap-2'} ${className}`}>
        {logo ? <img src={logo} alt="" className={`${large ? 'h-6 w-6' : 'h-5 w-5'} object-contain`} /> : icon} {t(label)}
      </button>
    );
  };

  const switchMode = (m) => { setMode(m); setSignupMethod('password'); setError(''); setMagicSent(false); };

  useEffect(() => {
    const token = searchParams.get('magic');
    if (!token || magicHandled.current || sessionStorage.getItem('magic_handled') === token) return;
    magicHandled.current = true;
    sessionStorage.setItem('magic_handled', token);
    setLoading(true);
    loginWithMagicToken(token)
      .then((result) => { if (!result?.mfa_required) navigate('/medlem', { replace: true }); })
      .catch((err) => { setMode('magic'); setError(formatApiError(err, 'Lenka er ugyldig.')); checkAuth(); })
      .finally(() => setLoading(false));
  }, [searchParams, loginWithMagicToken, navigate, checkAuth]);

  const submitMagic = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) {
      setError(t('Skriv inn e-postadressa di.'));
      return;
    }

    setLoading(true);
    try {
      await requestMagicLink(email);
      setMagicSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const openPasswordReset = (initialEmail = email) => {
    setResetEmail(initialEmail);
    setResetCode('');
    setResetToken('');
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetError('');
    setResetNotice('');
    setResetStage('request');
    setResetOpen(true);
  };

  const submitPasswordResetRequest = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetBusy(true);
    try {
      await api.post('/auth/password-reset/request', { email: resetEmail });
      setResetStage('verify');
      setResetNotice(t('Viss adressa har ein konto, er ein kode send. Han verkar i 10 minutt.'));
    } catch (err) {
      setResetError(formatApiError(err));
    } finally {
      setResetBusy(false);
    }
  };

  const submitPasswordResetCode = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetBusy(true);
    try {
      const { data } = await api.post('/auth/password-reset/verify', { email: resetEmail, code: resetCode });
      setResetToken(data.reset_token);
      setResetStage('password');
      setResetNotice('');
    } catch (err) {
      setResetError(formatApiError(err));
    } finally {
      setResetBusy(false);
    }
  };

  const submitPasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');
    if (resetPassword.length < 6) {
      setResetError(t('Passord må vera minst 6 teikn.'));
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setResetError(t('Passorda er ikkje like.'));
      return;
    }
    setResetBusy(true);
    try {
      await api.post('/auth/password-reset/complete', { reset_token: resetToken, new_password: resetPassword });
      setResetStage('done');
      setResetNotice(t('Passordet er endra. Du kan logge inn med det nye passordet.'));
      setResetPassword('');
      setResetPasswordConfirm('');
    } catch (err) {
      setResetError(formatApiError(err));
    } finally {
      setResetBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || (mode === 'signup' && (!name || !username)) || (signupMethod === 'password' && !password)) {
      setError(t('Vennligst fyll ut alle felt.'));
      return;
    }

    if (signupMethod === 'password' && password.length < 6) {
      setError(t('Passord må vera minst 6 teikn.'));
      return;
    }
    if (signupMethod === 'passkey' && (!window.PublicKeyCredential || !navigator.credentials?.create)) {
      setError(t('Nettlesaren støttar ikkje passnøklar.'));
      return;
    }

    setLoading(true);
    try {
      if (signupMethod === 'passkey') await registerWithPasskey(email, name, username);
      else await register(email, name, password, username);
      navigate('/medlem');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const beginEmailLogin = () => {
    setEmailLoginIdentifier(email);
    setEmailLoginPassword('');
    setEmailLoginError('');
    setEmailLoginStage('identifier');
    setEmailLoginOpen(true);
  };

  const submitEmailLogin = async (event) => {
    event.preventDefault();
    setEmailLoginError('');
    if (!emailLoginIdentifier.trim()) {
      setEmailLoginError(t('Skriv inn e-post eller brukarnamn.'));
      return;
    }

    if (emailLoginStage === 'password') {
      setEmailLoginBusy(true);
      try {
        const result = await login(emailLoginIdentifier.trim(), emailLoginPassword);
        setEmailLoginOpen(false);
        if (!result?.mfa_required) navigate('/medlem');
      } catch (err) {
        setEmailLoginError(formatApiError(err));
      } finally {
        setEmailLoginBusy(false);
      }
      return;
    }

    if (!window.PublicKeyCredential || !navigator.credentials?.get) {
      setEmailLoginError(t('Nettlesaren støttar ikkje passnøklar. Logg inn med passord.'));
      setEmailLoginStage('password');
      return;
    }

    setEmailLoginBusy(true);
    try {
      const result = await loginWithPasskey(emailLoginIdentifier.trim());
      setEmailLoginOpen(false);
      if (!result?.mfa_required) navigate('/medlem');
    } catch (err) {
      if (err.response?.status === 401 || err.name === 'NotAllowedError') {
        setEmailLoginStage('password');
        setEmailLoginError(t('Fann inga godkjend passnøkkel. Skriv passordet ditt for å halde fram.'));
      } else {
        setEmailLoginError(formatApiError(err, 'Kunne ikkje starte passnøkkelinnlogginga.'));
      }
    } finally {
      setEmailLoginBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-black overflow-hidden" data-testid="login-page">
      <Seo
        title={t('Logg inn | Refuel Weekend')}
        description={t('Logg inn eller registrer deg for Refuel Weekend 2027. Hald profilen din oppdatert før helga i Hjeltefjorden Arena.')}
        path="/login"
      />

      <div className="absolute inset-0" style={{ backgroundImage: `url(${mockData.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-black/95" />

      <Link to="/" data-testid="login-back-link" className="absolute top-6 left-6 z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm">
        <ArrowLeft size={16} /> {t('Tilbake')}
      </Link>

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size="md" /></div>

          <div className="rounded-3xl bg-[#0f0a17]/95 backdrop-blur-xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/50">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-2" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
                {t(mode === 'login' ? 'Velkomen tilbake' : mode === 'signup' ? 'Lag konto' : 'Logg inn utan passord')}
              </h1>
              <p className="text-white/60">
                {t(mode === 'login' ? 'Logg inn på medlemssida di' : mode === 'signup' ? 'Registrer deg for Refuel Weekend' : 'Vi sender deg ei innloggingslenke på e-post')}
              </p>
            </div>

            <div className="flex gap-1 p-1 rounded-full bg-white/5 mb-6">
              <button type="button" data-testid="login-tab" onClick={() => switchMode('login')} className={`flex-1 py-2.5 rounded-full font-bold text-sm transition-colors ${mode === 'login' ? 'bg-[#ff2e2e] text-black' : 'text-white/60 hover:text-white'}`}>{t('Logg inn')}</button>
              <button type="button" data-testid="signup-tab" onClick={() => switchMode('signup')} className={`flex-1 py-2.5 rounded-full font-bold text-sm transition-colors ${mode === 'signup' ? 'bg-[#ff2e2e] text-black' : 'text-white/60 hover:text-white'}`}>{t('Registrer')}</button>
              <button type="button" data-testid="magic-tab" onClick={() => switchMode('magic')} className={`flex-1 py-2.5 rounded-full font-bold text-sm transition-colors ${mode === 'magic' ? 'bg-[#ff2e2e] text-black' : 'text-white/60 hover:text-white'}`}>{t('E-postlenke')}</button>
            </div>

            {oauthProvidersUnavailable && <p role="status" data-testid="oauth-provider-status" className="mb-5 rounded-xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-center text-sm text-amber-200">{t('Innloggingsserveren svarer ikkje akkurat no. Google og andre innloggingsmetodar er mellombels utilgjengelege.')}</p>}

            {visibleOAuthMethods.length > 0 && (
              <>
                <div className="space-y-3">
                  {sortByMethodOrder(visibleOAuthMethods.filter(({ id }) => methodSettingsByProvider.get(id)?.size === 'large')).map(renderOAuthButton)}
                  {visibleOAuthMethods.some(({ id }) => methodSettingsByProvider.get(id)?.size !== 'large') && (
                    <div className="grid grid-cols-2 gap-3">
                      {sortByMethodOrder(visibleOAuthMethods.filter(({ id }) => methodSettingsByProvider.get(id)?.size !== 'large')).map(renderOAuthButton)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 my-6 text-white/30 text-xs uppercase tracking-wider">
                  <div className="flex-1 h-px bg-white/10" /> {t('eller med e-post')} <div className="flex-1 h-px bg-white/10" />
                </div>
              </>
            )}

            {mode === 'magic' ? (
              magicSent ? (
                <div className="text-center py-4" data-testid="magic-sent">
                  <MailCheck size={48} className="text-[#ff2e2e] mx-auto mb-3" />
                  <p className="text-white font-bold text-lg">{t('Sjekk innboksen din!')}</p>
                  <p className="text-white/60 text-sm mt-2">
                    {t('Vi har sendt ei innloggingslenke til')} <span className="text-white">{email}</span>. {t('Lenka verkar i 15 minutt.')}
                  </p>
                  <button type="button" onClick={() => setMagicSent(false)} className="mt-5 text-[#ff2e2e] text-sm font-bold hover:underline">{t('Send på nytt')}</button>
                </div>
              ) : (
                <form onSubmit={submitMagic} className="space-y-4" data-testid="magic-form">
                  <div>
                    <label className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">{t('E-post')}</label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input data-testid="magic-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('deg@refuel.no')} className={inputCls} required />
                    </div>
                  </div>

                  {error && <div data-testid="login-error" className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>}

                  <button type="submit" data-testid="magic-submit-button" disabled={loading} className="w-full py-4 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/30 disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading ? t('Vent...') : t('Send innloggingslenke')} {!loading && <Wand2 size={18} />}
                  </button>
                  <p className="text-white/40 text-xs text-center">{t('Har du ikkje konto frå før, blir han oppretta automatisk.')}</p>
                </form>
              )
            ) : mode === 'login' ? (
              <div className="space-y-4">
                <p className="text-center text-sm text-white/55">{t('Skriv inn e-post eller brukarnamn. Vi prøver passnøkkelen din først.')}</p>
                <button type="button" data-testid="email-login-open-button" onClick={beginEmailLogin} className="w-full py-4 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/30 flex items-center justify-center gap-2">
                  <Mail size={18} /> {t('Logg inn med e-post')}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {mode === 'signup' && (
                  <div>
                    <label className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">{t('Namn')}</label>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input data-testid="signup-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Ola Nordmann')} className={inputCls} />
                    </div>
                  </div>
                )}

                {mode === 'signup' && (
                  <div>
                    <label className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">{t('Brukarnamn')}</label>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input data-testid="signup-username-input" type="text" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))} placeholder="refuelmedlem" minLength={3} maxLength={32} pattern="[a-z0-9._-]{3,32}" className={inputCls} required />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">{t('E-post')}</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <input data-testid="login-email-input" type={mode === 'login' ? 'text' : 'email'} autoComplete={mode === 'login' ? 'username' : 'email'} value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t(mode === 'login' ? 'E-post eller brukarnamn' : 'deg@refuel.no')} className={inputCls} required />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 rounded-2xl bg-white/5 p-1" role="group" aria-label={t('Vel innloggingsmetode')}>
                  <button type="button" data-testid="signup-password-method" onClick={() => setSignupMethod('password')} aria-pressed={signupMethod === 'password'} className={`rounded-xl px-3 py-2.5 text-sm font-bold transition-colors ${signupMethod === 'password' ? 'bg-[#ff2e2e] text-black' : 'text-white/65 hover:text-white'}`}>{t('Passord')}</button>
                  <button type="button" data-testid="signup-passkey-method" onClick={() => setSignupMethod('passkey')} aria-pressed={signupMethod === 'passkey'} className={`rounded-xl px-3 py-2.5 text-sm font-bold transition-colors ${signupMethod === 'passkey' ? 'bg-[#ff2e2e] text-black' : 'text-white/65 hover:text-white'}`}>{t('Passnøkkel')}</button>
                </div>

                {signupMethod === 'password' ? (
                  <div>
                    <label className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">{t('Passord')}</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input data-testid="login-password-input" type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`${inputCls} pr-12`} required />
                      <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" aria-label={t('vis passord')}>
                        {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="-mt-2 text-sm text-white/55">{t('Kontoen blir oppretta når du godkjenner passnøkkelen. Du treng ikkje passord.')}</p>
                )}

                {error && <div data-testid="login-error" className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>}

                <button type="submit" data-testid="signup-submit-button" disabled={loading} className="w-full py-4 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/30 disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading ? t('Vent...') : t('Opprett konto')}
                  {!loading && (signupMethod === 'passkey' ? <KeyRound size={18} /> : <ArrowRight size={18} />)}
                </button>
              </form>
            )}

            <div className="mt-6 pt-6 border-t border-white/10 text-center text-white/50 text-sm">
              {mode === 'login' ? (
                <>
                  {t('Har du ikkje konto?')}{' '}
                  <button onClick={() => switchMode('signup')} className="text-[#ff2e2e] font-bold hover:underline">{t('Registrer deg')}</button>
                </>
              ) : (
                <>
                  {t('Har du allereie konto?')}{' '}
                  <button onClick={() => switchMode('login')} className="text-[#ff2e2e] font-bold hover:underline">{t('Logg inn')}</button>
                </>
              )}
            </div>
          </div>

          <p className="text-center text-white/40 text-xs mt-6">
            {t('Ved å halde fram godkjenner du')} <Link to="/p-policy" className="text-white/65 hover:text-white underline">{t('personvernerklæringa')}</Link>.
          </p>

          <Dialog open={resetOpen} onOpenChange={setResetOpen}>
            <DialogContent data-testid="password-reset-dialog" className="w-[calc(100vw-2rem)] max-w-md bg-[#0f0a17] border-white/10 text-white rounded-2xl [&>button]:text-white">
              <DialogTitle className="pr-8 text-2xl font-bold" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
                {resetStage === 'done' ? t('Passordet er endra') : t('Glemt passord?')}
              </DialogTitle>
              <DialogDescription className="text-white/60">
                {resetStage === 'request' && t('Skriv inn e-postadressa di, så sender vi ein eingongskode.')}
                {resetStage === 'verify' && t('Skriv inn den seks-sifra koden vi sende til e-posten din.')}
                {resetStage === 'password' && t('Vel eit nytt passord for kontoen din.')}
                {resetStage === 'done' && resetNotice}
              </DialogDescription>

              {resetStage === 'request' && (
                <form onSubmit={submitPasswordResetRequest} className="space-y-4">
                  <input data-testid="password-reset-email" type="email" required autoComplete="email" value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} placeholder={t('deg@refuel.no')} className={inputCls} />
                  {resetError && <p role="alert" className="text-sm text-red-300">{resetError}</p>}
                  <button type="submit" disabled={resetBusy} className="w-full rounded-full bg-[#ff2e2e] py-3 font-bold text-black disabled:opacity-60">{resetBusy ? t('Vent...') : t('Send kode')}</button>
                </form>
              )}

              {resetStage === 'verify' && (
                <form onSubmit={submitPasswordResetCode} className="space-y-4">
                  <p role="status" className="text-sm text-emerald-300">{resetNotice}</p>
                  <input data-testid="password-reset-code" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoComplete="one-time-code" required value={resetCode} onChange={(event) => setResetCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" className={`${inputCls} text-center text-2xl tracking-[0.3em]`} />
                  {resetError && <p role="alert" className="text-sm text-red-300">{resetError}</p>}
                  <button type="submit" disabled={resetBusy || resetCode.length !== 6} className="w-full rounded-full bg-[#ff2e2e] py-3 font-bold text-black disabled:opacity-60">{resetBusy ? t('Kontrollerer…') : t('Verifiser kode')}</button>
                  <button type="button" disabled={resetBusy} onClick={() => { setResetStage('request'); setResetCode(''); setResetError(''); setResetNotice(''); }} className="w-full py-2 text-sm font-semibold text-white/60 hover:text-white">{t('Send ny kode')}</button>
                </form>
              )}

              {resetStage === 'password' && (
                <form onSubmit={submitPasswordReset} className="space-y-4">
                  <input data-testid="password-reset-new-password" type="password" minLength={6} maxLength={128} autoComplete="new-password" required value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} placeholder={t('Nytt passord')} className={inputCls} />
                  <input data-testid="password-reset-confirm-password" type="password" minLength={6} maxLength={128} autoComplete="new-password" required value={resetPasswordConfirm} onChange={(event) => setResetPasswordConfirm(event.target.value)} placeholder={t('Gjenta nytt passord')} className={inputCls} />
                  {resetError && <p role="alert" className="text-sm text-red-300">{resetError}</p>}
                  <button type="submit" disabled={resetBusy} className="w-full rounded-full bg-[#ff2e2e] py-3 font-bold text-black disabled:opacity-60">{resetBusy ? t('Lagrar…') : t('Lagre nytt passord')}</button>
                </form>
              )}

              {resetStage === 'done' && <button type="button" onClick={() => { setResetOpen(false); setMode('login'); }} className="w-full rounded-full bg-[#ff2e2e] py-3 font-bold text-black">{t('Til innlogging')}</button>}
            </DialogContent>
          </Dialog>
          <Dialog open={emailLoginOpen} onOpenChange={setEmailLoginOpen}>
            <DialogContent data-testid="email-login-dialog" className="w-[calc(100vw-2rem)] max-w-md bg-[#0f0a17] border-white/10 text-white rounded-2xl [&>button]:text-white">
              <DialogTitle className="pr-8 text-2xl font-bold" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
                {t('Logg inn med e-post')}
              </DialogTitle>
              <DialogDescription className="text-white/60">
                {emailLoginStage === 'identifier'
                  ? t('Skriv e-post eller brukarnamn. Vi opnar passnøkkelen om kontoen har ei.')
                  : t('Ingen passnøkkel vart godkjend. Du kan logge inn med passord.')}
              </DialogDescription>
              <form onSubmit={submitEmailLogin} className="space-y-4">
                <div>
                  <label htmlFor="email-login-identifier" className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">{t('E-post eller brukarnamn')}</label>
                  <input id="email-login-identifier" data-testid="email-login-identifier" type="text" autoComplete="username" required value={emailLoginIdentifier} onChange={(event) => setEmailLoginIdentifier(event.target.value)} className={inputCls} />
                </div>
                {emailLoginStage === 'password' && (
                  <>
                    <div>
                      <label htmlFor="email-login-password" className="block text-xs font-bold text-white/70 mb-1.5 uppercase tracking-wider">{t('Passord')}</label>
                      <input id="email-login-password" data-testid="email-login-password" type="password" autoComplete="current-password" required value={emailLoginPassword} onChange={(event) => setEmailLoginPassword(event.target.value)} className={inputCls} />
                    </div>
                    <div className="text-right"><button type="button" onClick={() => { setEmailLoginOpen(false); openPasswordReset(emailLoginIdentifier); }} className="text-sm font-semibold text-[#ff2e2e] hover:underline">{t('Glemt passord?')}</button></div>
                  </>
                )}
                {emailLoginError && <p role="alert" data-testid="email-login-error" className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-red-300 text-sm">{emailLoginError}</p>}
                <button type="submit" data-testid="email-login-submit" disabled={emailLoginBusy} className="w-full py-4 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {emailLoginBusy ? t('Vent...') : emailLoginStage === 'password' ? t('Logg inn') : t('Fortsett')}
                  {!emailLoginBusy && (emailLoginStage === 'password' ? <ArrowRight size={18} /> : <KeyRound size={18} />)}
                </button>
                {emailLoginStage === 'password' && <button type="button" onClick={() => { setEmailLoginStage('identifier'); setEmailLoginPassword(''); setEmailLoginError(''); }} className="w-full py-2 text-sm font-semibold text-white/60 hover:text-white">{t('Prøv passnøkkel igjen')}</button>}
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Login;
